import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, CheckCircle, XCircle, AlertTriangle, Edit, Eye, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import ManufacturingService from '@/api/manufacturing.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface QualityCheck {
  id: number;
  publicId: string;
  businessId: number;
  checkNumber: string;
  productionJobId: number;
  productionJobNumber: string;
  recipeName: string;
  checkType: 'incoming' | 'in_process' | 'final' | 'random';
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'on_hold';
  inspector?: string;
  checkDate: string;
  sampleSize: number;
  passedCount: number;
  failedCount: number;
  defectTypes?: string[];
  notes?: string;
  corrective_action?: string;
  dateCreated: string;
  dateUpdated: string;
}

const QualityControl: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [qualityChecks, setQualityChecks] = useState<QualityCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchQualityChecks();
  }, [businessId]);

  const fetchQualityChecks = async () => {
    try {
      setLoading(true);
      const response = await ManufacturingService.getQualityChecks(businessId);
      
      if (response.data.result === 'success') {
        setQualityChecks(response.data?.qualityChecks || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch quality checks",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching quality checks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch quality checks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (check: QualityCheck, status: string, notes?: string) => {
    if (!canEdit('Manufacturing')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to update quality checks",
        variant: "destructive",
      });
      return;
    }

    try {
      await ManufacturingService.updateQualityCheckStatus(check.publicId, { status, notes });
      toast({
        title: "Success",
        description: "Quality check status updated successfully",
      });
      fetchQualityChecks();
    } catch (error) {
      console.error('Error updating quality check status:', error);
      toast({
        title: "Error",
        description: "Failed to update quality check status",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'outline' as const, label: 'Pending', icon: AlertTriangle },
      in_progress: { variant: 'secondary' as const, label: 'In Progress', icon: Search },
      passed: { variant: 'default' as const, label: 'Passed', icon: CheckCircle },
      failed: { variant: 'destructive' as const, label: 'Failed', icon: XCircle },
      on_hold: { variant: 'secondary' as const, label: 'On Hold', icon: AlertTriangle },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeLabel = (type: string) => {
    const typeLabels = {
      incoming: 'Incoming',
      in_process: 'In-Process',
      final: 'Final',
      random: 'Random'
    };
    return typeLabels[type as keyof typeof typeLabels] || type;
  };

  const getPassRate = (check: QualityCheck) => {
    const total = check.passedCount + check.failedCount;
    return total > 0 ? Math.round((check.passedCount / total) * 100) : 0;
  };

  const filteredChecks = qualityChecks.filter(check => {
    const matchesSearch = check.checkNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         check.productionJobNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         check.recipeName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || check.status === statusFilter;
    const matchesType = !typeFilter || check.checkType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quality Control</h1>
          <p className="text-muted-foreground">
            Manage quality checks and inspections for manufacturing
          </p>
        </div>
        {canCreate('Manufacturing') && (
          <Button onClick={() => navigate('/manufacturing/quality-control/create')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Quality Check
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Checks</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredChecks.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Passed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredChecks.filter(check => check.status === 'passed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredChecks.filter(check => check.status === 'failed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Pass Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredChecks.length > 0 ? Math.round(
                filteredChecks.reduce((sum, check) => sum + getPassRate(check), 0) / filteredChecks.length
              ) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search quality checks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="on_hold">On Hold</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Types</option>
              <option value="incoming">Incoming</option>
              <option value="in_process">In-Process</option>
              <option value="final">Final</option>
              <option value="random">Random</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Quality Checks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Quality Checks ({filteredChecks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Check Number</TableHead>
                    <TableHead>Production Job</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sample Size</TableHead>
                    <TableHead>Pass Rate</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChecks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No quality checks found</p>
                          {canCreate('Manufacturing') && (
                            <Button onClick={() => navigate('/manufacturing/quality-control/create')} size="sm">
                              Create your first quality check
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredChecks.map((check) => (
                      <TableRow key={check.id}>
                        <TableCell>
                          <div className="font-medium">{check.checkNumber}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{check.productionJobNumber}</div>
                            <div className="text-sm text-muted-foreground">{check.recipeName}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getTypeLabel(check.checkType)}</Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(check.status)}
                        </TableCell>
                        <TableCell>
                          {check.sampleSize}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">
                              {check.passedCount}/{check.passedCount + check.failedCount}
                            </div>
                            <Badge variant={getPassRate(check) >= 95 ? "default" : getPassRate(check) >= 80 ? "secondary" : "destructive"}>
                              {getPassRate(check)}%
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {check.inspector || 'Unassigned'}
                        </TableCell>
                        <TableCell>
                          {new Date(check.checkDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <div className="h-4 w-4">⋮</div>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Manufacturing') && (
                                <DropdownMenuItem onClick={() => navigate(`/manufacturing/quality-control/${check.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {canEdit('Manufacturing') && (
                                <>
                                  <DropdownMenuItem onClick={() => navigate(`/manufacturing/quality-control/${check.publicId}/edit`)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Check
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {check.status === 'pending' && (
                                    <DropdownMenuItem onClick={() => handleStatusUpdate(check, 'in_progress')}>
                                      Start Inspection
                                    </DropdownMenuItem>
                                  )}
                                  {check.status === 'in_progress' && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleStatusUpdate(check, 'passed')}>
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        Mark as Passed
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusUpdate(check, 'failed')}>
                                        <XCircle className="mr-2 h-4 w-4" />
                                        Mark as Failed
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusUpdate(check, 'on_hold')}>
                                        <AlertTriangle className="mr-2 h-4 w-4" />
                                        Put on Hold
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QualityControl; 